'use client'

import React from 'react'

type InputProps = React.HTMLAttributes<HTMLDivElement> & {
  disabled?: boolean
  isError?: boolean
  size?: 'sm' | 'md'
  required?: boolean
}

const InputContext = React.createContext<{
  disabled?: boolean
  isError?: boolean
  inputValue?: string
  setInputValue?: (value: string) => void
  size?: 'sm' | 'md'
  required?: boolean
}>({
  disabled: false,
  isError: false,
  inputValue: '',
  setInputValue: () => {},
  size: 'md',
  required: false
})

const Input = ({
  children,
  disabled,
  isError,
  className = '',
  size = 'md',
  required = false,
  ...props
}: InputProps) => {
  const [inputValue, setInputValue] = React.useState('')

  const contextValue = React.useMemo(
    () => ({ disabled, isError, inputValue, setInputValue, size, required }),
    [disabled, isError, inputValue, setInputValue, size, required]
  )

  return (
    <InputContext.Provider value={contextValue}>
      <div className={`grid grid-cols-1 gap-1 ${className}`} {...props}>
        {children}
      </div>
    </InputContext.Provider>
  )
}

type InputFieldProps = React.InputHTMLAttributes<HTMLInputElement>

const InputField = ({ className = '', value, ...props }: InputFieldProps) => {
  const context = React.useContext(InputContext)

  if (!context) {
    throw new Error('InputField must be used within a Input component')
  }

  const { isError, inputValue, disabled, setInputValue, size, required } =
    context

  const errorClassName =
    isError && !!inputValue
      ? 'bg-critical-red-50 text-critical-red-600'
      : 'bg-neutral-gray-25 text-neutral-gray-800 focus:bg-biome-green-50'

  const sizeClassName =
    size === 'sm'
      ? 'text-s-medium tablet-lg:body-xs-medium'
      : 'text-m-medium tablet-lg:body-sm-medium'

  React.useEffect(() => {
    setInputValue?.(value ? `${value}` : '')
  }, [value, setInputValue])

  return (
    <input
      className={`rounded-lg px-2 py-3 w-full ${sizeClassName} ${errorClassName} placeholder:text-neutral-gray-200 focus:outline-none focus:ring-0 disabled:bg-neutral-gray-100 ${className}`}
      disabled={disabled}
      value={value}
      {...props}
      required={required}
    />
  )
}

type InputFieldWrapperProps = React.HTMLAttributes<HTMLDivElement>

const InputFieldWrapper = ({
  className = '',
  children,
  ...props
}: InputFieldWrapperProps) => {
  const context = React.useContext(InputContext)

  if (!context) {
    throw new Error('InputFieldWrapper must be used within a Input component')
  }

  const { isError, inputValue, disabled } = context

  const errorClassName =
    isError && !!inputValue
      ? '!bg-critical-red-50 text-critical-red-600 [&_svg]:text-neutral-gray-700'
      : 'text-neutral-gray-800 focus-within:bg-biome-green-50'

  const disableClassName = disabled
    ? 'cursor-not-allowed !bg-neutral-gray-100'
    : 'cursor-auto'
  const hasMultipleChildren = React.Children.count(children) > 1
  const paddingClassName = hasMultipleChildren ? 'px-2' : 'px-0'

  return (
    <div
      className={`input-wrapper flex items-center rounded-lg bg-neutral-gray-25 ${paddingClassName} body-sm-medium ${errorClassName} ${disableClassName} focus-within:outline-none focus:ring-0 w-fit ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

type InputLabelProps = React.LabelHTMLAttributes<HTMLLabelElement>

const InputLabel = ({
  className = '',
  children,
  ...props
}: InputLabelProps) => {
  const context = React.useContext(InputContext)

  if (!context) {
    throw new Error('InputLabel must be used within a Input component')
  }

  const { isError, inputValue, required } = context

  return (
    <label
      className={`text-s-regular tablet-lg:body-xs-regular ${isError && !!inputValue ? 'text-critical-red-700' : 'text-neutral-gray-700'} ${className}`}
      {...props}
    >
      {children}
      {required && <span className="text-critical-red-700">*</span>}
    </label>
  )
}

type InputHelperTextProps = React.HTMLAttributes<HTMLDivElement>

const InputHelperText = ({
  className = '',
  ...props
}: InputHelperTextProps) => {
  const context = React.useContext(InputContext)

  if (!context) {
    throw new Error('InputHelperText must be used within a Input component')
  }

  const { isError } = context

  return (
    <div
      className={`text-s-regular tablet-lg:body-xs-regular ${isError ? 'text-critical-red-600' : 'text-neutral-gray-600'} ${className}`}
      {...props}
    />
  )
}

Input.Label = InputLabel
Input.FieldWrapper = InputFieldWrapper
Input.Field = InputField
Input.HelperText = InputHelperText

export default Input
